import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canUse, canUseAny } from "./access";

const PartnerContext = createContext(null);
export const OPEN_BEFORE_VALIDATION = ["/partenaire", "/partenaire/entreprise"];

const mapProspect = (p) => ({
  ...p, type: p.type || "moe", source: p.source || "Partenaire", city: p.city || "Vesoul",
  project: p.project || "Projet à qualifier", budget: p.budget || "", createdAt: (p.created_at || "").slice(0, 10),
  nextAction: p.next_action || "À planifier",
});

const ACTIVATION_STATUS_LABELS = {
  ACTIF: "Actif", ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE: "Actif", SUSPENDU: "Suspendu", RESILIE: "Résilié",
};
const VALIDATED_STATUSES = ["ACTIF", "ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE"];
export const ONBOARDING_STAGE_LABELS = {
  CANDIDATURE: "Candidature en cours d'examen par CozyHome Central",
  VALIDE: "Dossier validé — en attente du choix de votre secteur",
  TERRITOIRE_CHOISI: "Secteur choisi — en attente de la finalisation de votre abonnement",
  PAIEMENT_EN_ATTENTE: "Abonnement en attente de règlement",
  DOCUMENTS_EN_ATTENTE: "Documents complémentaires en attente",
  SUSPENDU: "Votre espace est actuellement suspendu — contactez CozyHome Central",
  RESILIE: "Votre contrat partenaire est résilié",
};

const frDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

export const DOC_STATUS_LABELS = { a_fournir: "À fournir", envoye: "En vérification", valide: "Validé", refuse: "Refusé" };
export const CHECK_STATUS_LABELS = { a_examiner: "En vérification", conforme: "Validé", bloquant: "Refusé" };

// Construit la liste de documents/vérifications réellement affichés à partir des données
// backend (partner_applications.documents/checks) — aucune donnée fictive n'est utilisée ici.
export const buildRealDocs = (territorial) => [
  ...(territorial.documents || []).map((d) => ({
    id: d.id, name: d.name, status: DOC_STATUS_LABELS[d.status_code] || "À fournir",
    date: frDate(d.uploaded_at), note: d.rejection_reason,
  })),
  ...(territorial.checks || []).map((c) => ({
    id: c.id, name: c.name, status: CHECK_STATUS_LABELS[c.status_code] || "En vérification",
    date: null, note: c.status_code === "bloquant" ? (c.note || "Point bloquant signalé par CozyHome Central") : c.note,
  })),
];

export const PIPELINE_LABELS = { NOUVEAU: "Nouveau", ATTRIBUE: "Attribué", CONTACTE: "Contacté", RDV: "RDV", PROPOSITION_ENVOYEE: "Proposition envoyée", GAGNE: "Gagné", PERDU: "Perdu" };
export const PIPELINE_ORDER = ["NOUVEAU", "ATTRIBUE", "CONTACTE", "RDV", "PROPOSITION_ENVOYEE", "GAGNE", "PERDU"];
const ORIGIN_LABELS = { SITE_ESTIMATION: "Estimation en ligne", SITE_CONTACT: "Formulaire site", CALLBACK_REQUEST: "Demande de rappel", MANUAL_CENTRALE: "Créé par la Centrale", PARTNER_CREATED: "Apporté par vous" };

export const APPOINTMENT_TYPE_LABELS = {
  PREMIER_RDV: "Premier rendez-vous", RDV_PROJET: "Rendez-vous projet", VISITE_TERRAIN: "Visite terrain",
  VISITE_CHANTIER: "Visite chantier", RDV_ADMINISTRATIF: "Rendez-vous administratif", RDV_VISIO: "Rendez-vous visio",
  RDV_TELEPHONIQUE: "Rendez-vous téléphonique", AUTRE: "Autre",
};
export const APPOINTMENT_LOCATION_LABELS = {
  CHEZ_CLIENT: "Chez le prospect/client", AGENCE: "Agence", TERRAIN: "Terrain", CHANTIER: "Chantier",
  VISIO: "Visio", TELEPHONE: "Téléphone", AUTRE_ADRESSE: "Autre adresse",
};

const buildRealPartner = (territorial) => {
  const p = territorial.partner;
  const territoryNames = territorial.territories.map((t) => t.name).join(", ");
  const isExempt = p.subscription_type === "EXONERE";
  return {
    firstName: p.firstname, lastName: p.lastname,
    company: territorial.company?.name || p.company_name || "—",
    legalForm: "—", siret: p.siret || "—",
    code: territorial.company?.code || territorial.territories[0]?.code || "—",
    sector: territoryNames || "Attribué après validation",
    email: p.email, phone: p.phone,
    address: p.city ? `${p.postal_code || ""} ${p.city}`.trim() : "—",
    since: frDate(p.validated_at),
    subscription: {
      status: isExempt ? "Exonéré" : (ACTIVATION_STATUS_LABELS[p.activation_status] || "Actif"),
      amount: isExempt ? "0 € HT/mois" : `${p.monthly_price_ht} € HT / mois`,
      startedAt: frDate(p.subscribed_at),
      nextBilling: null,
      isExempt,
    },
  };
};

const mapTerritorialProspect = (p) => ({
  id: p.id, type: "moe", source: ORIGIN_LABELS[p.origin_source] || "CozyHome",
  originSource: p.origin_source, originPartnerId: p.origin_partner_id,
  status: ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routing_status) ? "En attente"
    : ["ACCEPTED", "NEIGHBOR_ACCEPTED"].includes(p.routing_status) ? (PIPELINE_LABELS[p.pipeline_stage] || "Attribué")
    : p.routing_status === "PARTNER_REFUSED" ? "Refusé" : "Perdu",
  routingStatus: p.routing_status, pipelineStage: p.pipeline_stage || "NOUVEAU", city: p.city || "—",
  project: p.project_type || "Projet à qualifier", budget: p.budget || "",
  createdAt: (p.created_at || "").slice(0, 10), phone: p.phone, email: p.email, address: p.address || "",
  name: (p.company_name || `${p.firstname || ""} ${p.lastname || ""}`.trim()),
  customerType: p.customer_type || "INDIVIDUAL", coOwnerFirstName: p.co_owner_first_name || "", coOwnerLastName: p.co_owner_last_name || "",
  companyName: p.company_name || "", companySiret: p.company_siret || "", contactFirstName: p.contact_first_name || "", contactLastName: p.contact_last_name || "",
  slaDeadline: p.sla_deadline,
  phase3ProjectId: p.phase3_project_id, followupPartnerId: p.followup_partner_id, followupStatus: p.followup_status || "NONE",
  clientProfileId: p.client_profile_id || null,
  nextAction: ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routing_status) ? "À accepter ou refuser"
    : (p.phase3_project_id ? "Projet client créé" : "Faire évoluer l'étape"),
});

export const PartnerProvider = ({ children }) => {
  const { user } = useAuth();
  const backend = true;
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState({ view: "OWNER" });
  const [territorial, setTerritorial] = useState(null);
  const [territorialLoading, setTerritorialLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const membership = user?.partner_membership;
  const internalRole = membership?.internal_role || "OWNER";
  const can = useCallback((permission) => canUse(membership, permission), [membership]);
  const canAny = useCallback((permissions) => canUseAny(membership, permissions), [membership]);

  const refreshTerritorial = useCallback(async () => {
    if (!user || internalRole !== "OWNER") { setTerritorial(null); setTerritorialLoading(false); return; }
    setTerritorialLoading(true);
    try {
      const { data } = await api.get("/partner-space/dashboard");
      setTerritorial(data);
    } catch (error) {
      setTerritorial(null);
    } finally {
      setTerritorialLoading(false);
    }
  }, [user, internalRole]);

  const refreshAccess = useCallback(async () => {
    if (!user) {
      setDashboard({ view: "OWNER" });
      setProspects([]);
      setClients([]);
      setTerritorial(null);
      setAppointments([]);
      return;
    }
    try {
      const dashboardResponse = await api.get("/phase3/partner/dashboard");
      setDashboard(dashboardResponse.data);
      if (canAny(["prospects.view_all", "prospects.view_assigned"])) {
        const prospectResponse = await api.get("/phase3/partner/prospects");
        setProspects(prospectResponse.data.map(mapProspect));
      } else setProspects([]);
      if (canAny(["clients.view_all", "clients.view_assigned"])) {
        const clientResponse = await api.get("/phase3/partner/clients");
        setClients(clientResponse.data);
      } else setClients([]);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
    await refreshTerritorial();
  }, [user, canAny, refreshTerritorial]);

  useEffect(() => { refreshAccess(); }, [refreshAccess]);

  const addProspect = useCallback(async (prospect) => {
    try {
      const { data } = await api.post("/phase3/partner/prospects", {
        name: prospect.name, email: prospect.email || null, phone: prospect.phone || null,
        status: prospect.status, next_action: prospect.nextAction || "Premier appel à planifier",
      });
      setProspects((list) => [mapProspect(data), ...list]);
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, []);

  const acceptTerritorialProspect = useCallback(async (prospectId) => {
    try {
      await api.post(`/partner-space/prospects/${prospectId}/accept`);
      toast.success("Projet accepté");
      await refreshTerritorial();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshTerritorial]);

  const refuseTerritorialProspect = useCallback(async (prospectId) => {
    try {
      await api.post(`/partner-space/prospects/${prospectId}/refuse`);
      toast.success("Projet refusé");
      await refreshTerritorial();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshTerritorial]);

  const createClientProjectFromProspect = useCallback(async (prospectId) => {
    try {
      const { data } = await api.post(`/partner-space/prospects/${prospectId}/create-client-project`);
      toast.success(data.already_exists ? "Projet client déjà existant" : "Projet client créé");
      await refreshTerritorial();
      return data.project;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    }
  }, [refreshTerritorial]);

  const validateProspectAsClient = useCallback(async (prospectId) => {
    try {
      const { data } = await api.post(`/partner-space/prospects/${prospectId}/validate-as-client`);
      toast.success(data.already_client ? "Ce prospect est déjà client" : "Prospect validé comme client — son Espace Client est en cours d'activation");
      await refreshTerritorial();
      return data.client;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    }
  }, [refreshTerritorial]);

  const createProjectForClient = useCallback(async (clientId, payload) => {
    try {
      const { data } = await api.post(`/partner-space/clients/${clientId}/projects`, payload);
      toast.success("Projet créé");
      await refreshAccess();
      return data.project;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    }
  }, [refreshAccess]);

  const createSmallWorkForClient = useCallback(async (clientId, payload) => {
    try {
      const { data } = await api.post(`/partner-space/clients/${clientId}/small-works`, payload);
      toast.success("Dossier Petits travaux créé");
      await refreshAccess();
      return data.small_work;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    }
  }, [refreshAccess]);

  const resendClientActivation = useCallback(async (clientId) => {
    try {
      await api.post(`/partner-space/clients/${clientId}/resend-activation`);
      toast.success("Email d'activation renvoyé");
      await refreshAccess();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshAccess]);

  const createOwnProspect = useCallback(async (payload) => {
    try {
      await api.post("/partner-space/prospects", payload);
      toast.success("Prospect créé — il vous appartient commercialement dès maintenant");
      await refreshTerritorial();
      return { ok: true };
    } catch (error) {
      const status = error.response?.status;
      const message = formatApiErrorDetail(error.response?.data?.detail);
      if (status !== 409) toast.error(message);
      return { ok: false, duplicate: status === 409, message };
    }
  }, [refreshTerritorial]);

  const signalDuplicate = useCallback(async (email, phone, note) => {
    try {
      await api.post("/partner-space/prospects/signal-duplicate", { email, phone, note });
      toast.success("La Centrale a été informée de votre demande de mise en relation");
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, []);

  const updatePipelineStage = useCallback(async (prospectId, stage) => {
    try {
      await api.patch(`/partner-space/prospects/${prospectId}/pipeline`, { pipeline_stage: stage });
      toast.success("Étape mise à jour");
      await refreshTerritorial();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshTerritorial]);

  const requestCollaboration = useCallback(async (prospectId, targetPartnerId) => {
    try {
      await api.post(`/partner-space/prospects/${prospectId}/request-collaboration`, { target_partner_id: targetPartnerId });
      toast.success("Demande de collaboration envoyée");
      await refreshTerritorial();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshTerritorial]);

  const respondCollaboration = useCallback(async (prospectId, accept) => {
    try {
      await api.post(`/partner-space/prospects/${prospectId}/collaboration/respond`, { accept });
      toast.success(accept ? "Collaboration acceptée" : "Collaboration refusée");
      await refreshTerritorial();
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshTerritorial]);

  const lookupTerritoryPartner = useCallback(async (postalCode, city) => {
    try {
      const { data } = await api.get("/partner-space/territories/lookup", { params: { postal_code: postalCode, city } });
      return data;
    } catch {
      return { found: false };
    }
  }, []);

  const refreshAppointments = useCallback(async () => {
    if (!user || internalRole !== "OWNER") { setAppointments([]); return; }
    try {
      const { data } = await api.get("/partner-space/appointments");
      setAppointments(data);
    } catch {
      setAppointments([]);
    }
  }, [user, internalRole]);

  useEffect(() => { refreshAppointments(); }, [refreshAppointments]);

  const createAppointment = useCallback(async (payload) => {
    try {
      await api.post("/partner-space/appointments", payload);
      toast.success("Rendez-vous créé");
      await Promise.all([refreshAppointments(), refreshTerritorial()]);
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshAppointments, refreshTerritorial]);

  const updateAppointment = useCallback(async (id, payload) => {
    try {
      await api.patch(`/partner-space/appointments/${id}`, payload);
      toast.success("Rendez-vous modifié");
      await Promise.all([refreshAppointments(), refreshTerritorial()]);
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshAppointments, refreshTerritorial]);

  const setAppointmentStatus = useCallback(async (id, status) => {
    try {
      await api.patch(`/partner-space/appointments/${id}/status`, { status });
      toast.success(status === "REALISE" ? "Rendez-vous marqué réalisé" : status === "ANNULE" ? "Rendez-vous annulé" : "Statut mis à jour");
      await Promise.all([refreshAppointments(), refreshTerritorial()]);
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshAppointments, refreshTerritorial]);

  const deleteAppointment = useCallback(async (id) => {
    try {
      await api.delete(`/partner-space/appointments/${id}`);
      toast.success("Rendez-vous supprimé");
      await Promise.all([refreshAppointments(), refreshTerritorial()]);
      return true;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return false;
    }
  }, [refreshAppointments, refreshTerritorial]);

  const name = user?.name?.split(" ") || [];
  const currentFirstName = name[0] || "";
  const currentLastName = name.slice(1).join(" ");
  const currentEmail = user?.email || "";
  const EMPTY_REAL_PARTNER = {
    firstName: currentFirstName, lastName: currentLastName, company: "—", legalForm: "—", siret: "—",
    code: "—", sector: "—", email: currentEmail, phone: "—", address: "—", since: "—",
    subscription: { status: "—", amount: "—", startedAt: "—", nextBilling: null, isExempt: false },
  };
  const partner = territorial ? buildRealPartner(territorial) : EMPTY_REAL_PARTNER;
  const validated = territorial ? VALIDATED_STATUSES.includes(territorial.partner.activation_status) : false;
  const territorialProspects = territorial ? territorial.prospects.map(mapTerritorialProspect) : null;
  const pendingProspects = territorial ? territorial.pending_prospects.map(mapTerritorialProspect) : [];
  const collaborationsIncoming = territorial ? territorial.collaborations_incoming.map(mapTerritorialProspect) : [];
  const refusedDocumentsCount = territorial ? territorial.documents.filter((d) => d.status_code === "refuse").length : 0;

  const value = useMemo(() => ({
    partner, currentUser: { firstName: currentFirstName, lastName: currentLastName, email: currentEmail }, validated,
    prospects, clients, addProspect, membership, internalRole, can, canAny,
    dashboard, refreshAccess, backend,
    territorial, territorialLoading, territorialProspects, territoryPendingActivation: territorial?.territory_pending_activation ?? null,
    acceptTerritorialProspect, refuseTerritorialProspect, createClientProjectFromProspect,
    validateProspectAsClient, createProjectForClient, createSmallWorkForClient, resendClientActivation,
    createOwnProspect, updatePipelineStage, requestCollaboration, respondCollaboration, lookupTerritoryPartner, signalDuplicate,
    pendingProspects, collaborationsIncoming, refusedDocumentsCount,
    appointments, createAppointment, updateAppointment, setAppointmentStatus, deleteAppointment, refreshAppointments,
  }), [partner, currentFirstName, currentLastName, currentEmail, backend, validated, prospects, clients, addProspect, membership, internalRole, can, canAny, dashboard, refreshAccess, territorial, territorialLoading, territorialProspects, acceptTerritorialProspect, refuseTerritorialProspect, createClientProjectFromProspect, validateProspectAsClient, createProjectForClient, createSmallWorkForClient, resendClientActivation, createOwnProspect, updatePipelineStage, requestCollaboration, respondCollaboration, lookupTerritoryPartner, signalDuplicate, pendingProspects, collaborationsIncoming, refusedDocumentsCount, appointments, createAppointment, updateAppointment, setAppointmentStatus, deleteAppointment, refreshAppointments]);

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
};

export const usePartner = () => useContext(PartnerContext);
